import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import { safeNext, stripParam, resolveRedirect } from "./safeNext";

// Re-export for existing consumers (e.g. Login.jsx `import { safeNext } from
// "@/auth/ProtectedRoute";`). The canonical source is `./safeNext`.
export { safeNext, stripParam, resolveRedirect };

function FullPageLoader({ testId = "auth-loader" }) {
  return (
    <div
      data-testid={testId}
      className="min-h-screen flex items-center justify-center bg-v2-background"
    >
      <div className="flex items-center gap-3 text-v2-textMuted text-sm">
        <span
          className="w-2 h-2 rounded-full animate-pulse"
          style={{ background: "var(--v2-accent)" }}
        />
        Loading…
      </div>
    </div>
  );
}

export function ProtectedRoute({ children, requireAdmin = false }) {
  const { user, loading, authenticated, isAdmin } = useAuth();
  const location = useLocation();

  if (loading) return <FullPageLoader />;
  if (!authenticated) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?next=${next}`} replace />;
  }
  if (requireAdmin && !isAdmin) {
    return <Navigate to="/dashboard?forbidden=1" replace />;
  }
  return typeof children === "function" ? children(user) : children;
}

export function AdminRoute({ children }) {
  return <ProtectedRoute requireAdmin>{children}</ProtectedRoute>;
}

export function PublicOnlyRoute({ children }) {
  // Used to redirect logged-in users away from /login & /signup.
  const { loading, authenticated, user } = useAuth();
  const location = useLocation();
  if (loading) return <FullPageLoader />;
  if (authenticated) {
    // Cluster B iter4 fix — single-branch resolver. Delegates all the
    // safe-next / rejected-next / merge-with-preserved-search logic to
    // `resolveRedirect()`, so referral codes (`?ref=`), utm tracking,
    // and any other outer-URL params survive on the destination URL
    // whether the `next` value was accepted, rejected, or absent.
    // Previous iter3 wiring dropped these params on the safe-next path
    // because `<Navigate to={next} />` (bare pathname) implicitly reset
    // the location to have no search.
    const params = new URLSearchParams(location.search);
    const { pathname: nextPath, search: mergedSearch } = resolveRedirect(
      params.get("next"),
      location.search,
    );
    const dest =
      nextPath || (user.funding_profile_complete ? "/dashboard" : "/profile");
    return (
      <Navigate
        to={{ pathname: dest, search: mergedSearch, hash: location.hash }}
        replace
      />
    );
  }
  return children;
}
